import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Sparkles } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import StarField from "@/components/StarField";
import { memorialsApi, paymentsApi, formatApiError } from "@/lib/api";
import { STAR_COLOR_OPTIONS } from "@/lib/starColors";
import { compressImage } from "@/lib/imageCompress";

const STEP_INTRO = 0;
const STEP_CODE = 1;
const STEP_AFTER_PAY = 2;
const STEP_FORM = 3;
const STEP_DONE = 4;

export default function MemorialCreate() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [step, setStep] = useState(STEP_INTRO);
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [issuedCode, setIssuedCode] = useState(null);
    const [issuedCodes, setIssuedCodes] = useState([]);
    const [usedCodes, setUsedCodes] = useState([]);
    const [buying, setBuying] = useState(false);
    const [polling, setPolling] = useState(false);
    const [savedSlug, setSavedSlug] = useState(null);

    // Memorial form
    const [name, setName] = useState("");
    const [story, setStory] = useState("");
    const [deathDate, setDeathDate] = useState("");
    const [location, setLocation] = useState("");
    const [message, setMessage] = useState("");
    const [ownerName, setOwnerName] = useState("");
    const [ownerEmail, setOwnerEmail] = useState("");
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [starColor, setStarColor] = useState("gold");
    const [submitting, setSubmitting] = useState(false);
    const [editToken, setEditToken] = useState(null);

    // Handle return from Stripe (session_id in URL)
    useEffect(() => {
        const sid = params.get("session_id");
        if (!sid) return;
        setPolling(true);
        let attempts = 0;
        const maxAttempts = 8;
        const interval = 1800;
        const poll = async () => {
            attempts += 1;
            try {
                const r = await paymentsApi.checkoutStatus(sid);
                if (r.status === "paid") {
                    const codes = Array.isArray(r.codes) && r.codes.length > 0
                        ? r.codes
                        : (r.code ? [r.code] : []);
                    setIssuedCode(codes[0] || null);
                    setIssuedCodes(codes);
                    setCode(codes[0] || "");
                    setStep(STEP_AFTER_PAY);
                    setPolling(false);
                    toast.success(
                        codes.length > 1
                            ? `Zahlung erfolgreich. ${codes.length} Codes wurden erstellt.`
                            : "Zahlung erfolgreich. Dein Code wurde erstellt."
                    );
                    return;
                }
                if (r.status === "expired" || r.status === "failed") {
                    setPolling(false);
                    toast.error("Zahlung nicht abgeschlossen.");
                    return;
                }
                if (attempts >= maxAttempts) {
                    setPolling(false);
                    toast.error("Zahlung wird noch verarbeitet. Du erhältst den Code per Mail.");
                    return;
                }
                setTimeout(poll, interval);
            } catch {
                setPolling(false);
                toast.error("Konnte Zahlungsstatus nicht prüfen.");
            }
        };
        poll();
    }, [params]);

    // Handle deep-link from email (?code=XXX) — jump straight to code-verification step
    useEffect(() => {
        const codeParam = params.get("code");
        if (codeParam && !params.get("session_id")) {
            setCode(codeParam.trim().toUpperCase());
            setStep(STEP_CODE);
        }
    }, [params]);

    async function buyCode(e) {
        e.preventDefault();
        if (!email) return toast.error("Bitte E-Mail eingeben");
        const qty = Math.max(1, Math.min(20, parseInt(quantity, 10) || 1));
        setBuying(true);
        try {
            const r = await paymentsApi.createMemorialCheckout(email, qty);
            window.location.href = r.url;
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setBuying(false);
        }
    }

    // Step over to the form with the first unused issued code
    function startEntryWithCode(c) {
        setCode(c);
        setStep(STEP_FORM);
    }

    // Compute unused codes (from issuedCodes minus already-used ones)
    const unusedCodes = issuedCodes.filter((c) => !usedCodes.includes(c));

    async function verifyCode(e) {
        e.preventDefault();
        const c = code.trim().toUpperCase();
        if (!c) return toast.error("Bitte Code eingeben");
        try {
            await memorialsApi.checkCode(c);
            setCode(c);
            setStep(STEP_FORM);
            toast.success("Code akzeptiert");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    }

    async function onFile(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        // Hard ceiling at 25 MB original (we'll compress below)
        if (f.size > 25 * 1024 * 1024) {
            return toast.error("Bild zu groß (max 25 MB)");
        }
        try {
            const compressed = await compressImage(f);
            setFile(compressed);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(compressed));
        } catch {
            // Fall back to original if compression fails
            setFile(f);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(f));
        }
    }

    async function submitMemorial(e) {
        e.preventDefault();
        if (!name.trim()) return toast.error("Bitte Name eingeben");
        if (!file) return toast.error("Bitte Bild auswählen");
        setSubmitting(true);
        try {
            const form = new FormData();
            form.append("code", code);
            form.append("name", name);
            form.append("story", story);
            if (deathDate) form.append("death_date", deathDate);
            if (location) form.append("location", location);
            if (message) form.append("message", message);
            if (ownerName) form.append("owner_name", ownerName);
            if (ownerEmail) form.append("owner_email", ownerEmail);
            if (starColor) form.append("star_color", starColor);
            form.append("image", file);
            const r = await memorialsApi.create(form);
            setSavedSlug(r.slug);
            if (r.edit_token) setEditToken(r.edit_token);
            // Mark the used code (only matters when multiple codes were bought together)
            setUsedCodes((prev) => (prev.includes(code) ? prev : [...prev, code]));
            setStep(STEP_DONE);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    // Reset form state so the user can enter the next star with another code
    function resetForNextEntry(nextCode) {
        setName("");
        setStory("");
        setDeathDate("");
        setLocation("");
        setMessage("");
        setOwnerName("");
        setOwnerEmail("");
        setFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setStarColor("gold");
        setSavedSlug(null);
        setEditToken(null);
        setCode(nextCode);
        setStep(STEP_FORM);
    }

    return (
        <SiteLayout>
            <section className="relative">
                <StarField density={50} className="absolute inset-0" />
                <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <Link
                        to="/taubenhimmel"
                        className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-10"
                    >
                        <ArrowLeft size={14} /> Zurück zum Himmel
                    </Link>

                    {polling && (
                        <div className="glass p-8 text-center mb-6" data-testid="payment-polling">
                            <div className="label gentle-pulse mb-2">Zahlung wird geprüft</div>
                            <p className="text-[#94a3b8]">
                                Einen Moment — wir warten auf die Bestätigung von Stripe.
                            </p>
                        </div>
                    )}

                    {step === STEP_INTRO && !polling && (
                        <div data-testid="step-intro">
                            <div className="label mb-4">Schritt 1 / 3</div>
                            <h1 className="font-display text-4xl sm:text-5xl mb-6 leading-tight">
                                Ihre Taube ihren Platz schenken
                            </h1>
                            <p className="text-[#94a3b8] leading-relaxed mb-10">
                                Für 2,00 € erhältst du einen einmaligen Eintrags-Code für eine
                                eigene Gedenkseite. 1,00 € fließt direkt in den Himmelsfond für
                                lebende Stadttauben, der Rest trägt die Plattform.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <ChoiceCard
                                    title="Code jetzt kaufen"
                                    text="2,00 € via Stripe Checkout. Du wirst sicher zur Bezahlung weitergeleitet."
                                    cta="Weiter zur Zahlung"
                                    testid="choice-buy"
                                    onClick={() => setStep(STEP_CODE)}
                                    primary
                                />
                                <ChoiceCard
                                    title="Code einlösen"
                                    text="Du hast bereits einen Code (z. B. von der Admin oder per Mail nach Zahlung)?"
                                    cta="Code eingeben"
                                    testid="choice-redeem"
                                    onClick={() => {
                                        setStep(STEP_CODE);
                                        setIssuedCode("redeem");
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {step === STEP_CODE && !polling && (
                        <div data-testid="step-code">
                            <div className="label mb-4">Schritt 2 / 3</div>
                            {issuedCode === "redeem" ? (
                                <>
                                    <h1 className="font-display text-4xl mb-6">Code einlösen</h1>
                                    <form onSubmit={verifyCode} className="space-y-4 max-w-md">
                                        <input
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.toUpperCase())}
                                            placeholder="XXXX-XXXX-XXXX"
                                            className="input-field font-mono tracking-widest text-lg"
                                            data-testid="code-input"
                                            autoComplete="off"
                                        />
                                        <button type="submit" className="btn-primary" data-testid="code-verify">
                                            Code prüfen
                                        </button>
                                    </form>
                                </>
                            ) : (
                                <>
                                    <h1 className="font-display text-4xl mb-6">Code(s) kaufen</h1>
                                    <p className="text-[#94a3b8] mb-8 leading-relaxed">
                                        Jeder Code gilt für eine Gedenkseite. Du kannst auch
                                        mehrere auf einmal kaufen — perfekt zum Verschenken
                                        oder für mehrere Tauben. Wir senden dir alle Codes
                                        per E-Mail zu, sobald die Zahlung erfolgreich war.
                                    </p>
                                    <form onSubmit={buyCode} className="space-y-5 max-w-md">
                                        <Field label="Anzahl der Codes (1–20)" required>
                                            <div className="flex items-stretch gap-3" data-testid="quantity-stepper">
                                                <button
                                                    type="button"
                                                    onClick={() => setQuantity((q) => Math.max(1, parseInt(q, 10) - 1))}
                                                    className="w-12 h-12 rounded-xl border border-white/10 text-lg hover:border-[#f0c674]/40 hover:text-[#f0c674] transition"
                                                    aria-label="Eine Anzahl weniger"
                                                    data-testid="quantity-decrement"
                                                >
                                                    −
                                                </button>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="20"
                                                    value={quantity}
                                                    onChange={(e) => {
                                                        const v = parseInt(e.target.value, 10);
                                                        if (Number.isNaN(v)) return setQuantity(1);
                                                        setQuantity(Math.max(1, Math.min(20, v)));
                                                    }}
                                                    className="input-field text-center text-lg font-mono flex-1"
                                                    data-testid="quantity-input"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setQuantity((q) => Math.min(20, parseInt(q, 10) + 1))}
                                                    className="w-12 h-12 rounded-xl border border-white/10 text-lg hover:border-[#f0c674]/40 hover:text-[#f0c674] transition"
                                                    aria-label="Eine Anzahl mehr"
                                                    data-testid="quantity-increment"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between text-sm" data-testid="quantity-summary">
                                                <span className="text-[#94a3b8]">
                                                    {quantity} × 2,00 €
                                                </span>
                                                <span className="text-[#f0c674] text-base font-medium">
                                                    Gesamt: {(quantity * 2).toFixed(2).replace(".", ",")} €
                                                </span>
                                            </div>
                                            <p className="text-xs text-[#64748b] mt-2">
                                                Davon fließen {quantity.toFixed(2).replace(".", ",")} €
                                                in den Himmelsfond für lebende Stadttauben.
                                            </p>
                                        </Field>
                                        <Field label="Deine E-Mail-Adresse" required>
                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="z. B. du@example.com"
                                                className="input-field"
                                                required
                                                data-testid="buy-email-input"
                                            />
                                        </Field>
                                        <button
                                            type="submit"
                                            className="btn-primary w-full"
                                            disabled={buying}
                                            data-testid="buy-checkout"
                                        >
                                            {buying ? "Bitte warten…" : `Weiter zu Stripe Checkout (${(quantity * 2).toFixed(2).replace(".", ",")} €) →`}
                                        </button>
                                    </form>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={() => setStep(STEP_INTRO)}
                                className="mt-6 text-[#64748b] hover:text-[#f0c674] text-sm"
                            >
                                ← Andere Option wählen
                            </button>
                        </div>
                    )}

                    {step === STEP_AFTER_PAY && !polling && (
                        <div data-testid="step-after-pay">
                            <div className="label mb-4">Zahlung erfolgreich</div>
                            <h1 className="font-display text-4xl sm:text-5xl mb-4 leading-tight">
                                {issuedCodes.length > 1
                                    ? `${issuedCodes.length} Sterne warten auf dich.`
                                    : "Dein Stern wartet auf dich."}
                            </h1>
                            <p className="text-[#94a3b8] leading-relaxed mb-8">
                                {issuedCodes.length > 1 ? (
                                    <>Du hast <span className="text-[#f0c674]">{issuedCodes.length} Eintragscodes</span> erhalten. Wir haben sie dir auch per E-Mail geschickt — du kannst sie also jederzeit später nutzen oder verschenken.</>
                                ) : (
                                    <>Dein Eintragscode ist bereit. Wir haben ihn dir auch per E-Mail geschickt — du kannst ihn also jederzeit später nutzen oder verschenken.</>
                                )}
                            </p>

                            <div className="glass p-5 mb-8 border-[#f0c674]/20" data-testid="after-pay-codes">
                                <div className="label mb-3">
                                    {issuedCodes.length > 1 ? "Deine Codes" : "Dein Code"}
                                </div>
                                <div className="space-y-2">
                                    {issuedCodes.map((c) => (
                                        <div
                                            key={c}
                                            className="flex items-center justify-between gap-3 bg-[#040710]/60 border border-white/10 rounded-lg px-4 py-3"
                                            data-testid={`after-pay-code-${c}`}
                                        >
                                            <code className="font-mono text-[#f0c674] tracking-wider text-sm sm:text-base break-all">
                                                {c}
                                            </code>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(c);
                                                    toast.success("Code kopiert");
                                                }}
                                                className="text-xs text-[#94a3b8] hover:text-[#f0c674] transition shrink-0"
                                                data-testid={`copy-code-${c}`}
                                            >
                                                Kopieren
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <ChoiceCard
                                    title="Jetzt einen Eintrag erstellen"
                                    text="Du füllst gleich das Formular aus und siehst deinen ersten Stern im Himmel leuchten."
                                    cta={issuedCodes.length > 1 ? `Mit erstem Code starten` : "Eintrag erstellen"}
                                    testid="after-pay-start"
                                    onClick={() => startEntryWithCode(issuedCodes[0])}
                                    primary
                                />
                                <ChoiceCard
                                    title={issuedCodes.length > 1 ? "Codes nur per Mail aufheben" : "Code nur per Mail aufheben"}
                                    text={
                                        issuedCodes.length > 1
                                            ? "Du nimmst alle Codes mit nach Hause — zum Verschenken oder für später."
                                            : "Du nimmst den Code mit nach Hause — zum Verschenken oder für später."
                                    }
                                    cta="Zum Taubenhimmel"
                                    testid="after-pay-keep"
                                    onClick={() => {
                                        toast.success(
                                            issuedCodes.length > 1
                                                ? "Alle Codes wurden dir per E-Mail geschickt."
                                                : "Dein Code wurde dir per E-Mail geschickt."
                                        );
                                        navigate("/taubenhimmel");
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {step === STEP_FORM && (
                        <div data-testid="step-form">
                            <div className="label mb-4">Schritt 3 / 3</div>
                            <h1 className="font-display text-4xl mb-2">Gedenkseite erstellen</h1>
                            <p className="text-[#94a3b8] mb-8">
                                Code akzeptiert: <span className="font-mono text-[#f0c674]">{code}</span>
                            </p>
                            <form onSubmit={submitMemorial} className="space-y-5">
                                <Field label="Name der Taube" required>
                                    <input
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="input-field"
                                        required
                                        data-testid="memorial-form-name"
                                    />
                                </Field>
                                <Field label="Foto" required>
                                    <label
                                        htmlFor="memorial-image"
                                        className="cursor-pointer flex items-center gap-3 input-field hover:border-[#f0c674]/40"
                                    >
                                        <Upload size={16} className="text-[#f0c674]" />
                                        <span className="text-sm text-[#94a3b8]">
                                            {file ? file.name : "Bild auswählen (max 6 MB)"}
                                        </span>
                                    </label>
                                    <input
                                        id="memorial-image"
                                        type="file"
                                        accept="image/*"
                                        onChange={onFile}
                                        className="hidden"
                                        data-testid="memorial-form-image"
                                    />
                                    {previewUrl && (
                                        <img
                                            src={previewUrl}
                                            alt="Vorschau"
                                            className="mt-3 w-32 h-32 object-cover rounded-xl"
                                        />
                                    )}
                                </Field>
                                <Field label="Kurze Geschichte">
                                    <textarea
                                        value={story}
                                        onChange={(e) => setStory(e.target.value)}
                                        className="input-field"
                                        rows={4}
                                        placeholder="Was war besonders an ihr/ihm?"
                                        data-testid="memorial-form-story"
                                    />
                                </Field>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                    <Field label="Datum (optional)">
                                        <input
                                            type="date"
                                            value={deathDate}
                                            onChange={(e) => setDeathDate(e.target.value)}
                                            className="input-field"
                                            data-testid="memorial-form-date"
                                        />
                                    </Field>
                                    <Field label="Ort (optional)">
                                        <input
                                            value={location}
                                            onChange={(e) => setLocation(e.target.value)}
                                            className="input-field"
                                            placeholder="z. B. Berlin Kreuzberg"
                                            data-testid="memorial-form-location"
                                        />
                                    </Field>
                                </div>
                                <Field label="Persönliche Nachricht (optional)">
                                    <textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        className="input-field"
                                        rows={3}
                                        data-testid="memorial-form-message"
                                    />
                                </Field>
                                <Field label="Dein Name (optional)">
                                    <input
                                        value={ownerName}
                                        onChange={(e) => setOwnerName(e.target.value)}
                                        className="input-field"
                                        data-testid="memorial-form-owner"
                                    />
                                </Field>
                                <Field label="Deine E-Mail (optional, für späteren Bearbeitungs-Link)">
                                    <input
                                        type="email"
                                        value={ownerEmail}
                                        onChange={(e) => setOwnerEmail(e.target.value)}
                                        className="input-field"
                                        placeholder="z. B. du@example.com"
                                        data-testid="memorial-form-email"
                                    />
                                    <p className="text-xs text-[#64748b] mt-1.5">
                                        Wir senden dir nach der Veröffentlichung einen persönlichen
                                        Link, mit dem du die Gedenkseite jederzeit ändern oder löschen kannst.
                                    </p>
                                </Field>
                                <Field label="In welcher Farbe soll der Stern leuchten?">
                                    <div className="grid grid-cols-4 sm:grid-cols-7 gap-3" data-testid="memorial-form-color">
                                        {STAR_COLOR_OPTIONS.map((c) => (
                                            <button
                                                type="button"
                                                key={c.key}
                                                onClick={() => setStarColor(c.key)}
                                                title={c.label}
                                                aria-label={c.label}
                                                className={`group flex flex-col items-center gap-2 p-2 rounded-xl transition-all ${
                                                    starColor === c.key
                                                        ? "bg-white/5 ring-1 ring-[#f0c674]"
                                                        : "hover:bg-white/5"
                                                }`}
                                                data-testid={`color-${c.key}`}
                                            >
                                                <span
                                                    className="w-7 h-7 rounded-full gentle-pulse"
                                                    style={{
                                                        background: c.hex,
                                                        boxShadow: `0 0 14px ${c.hex}B3, 0 0 4px ${c.hex}`,
                                                    }}
                                                />
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-[#94a3b8] group-hover:text-[#f8fafc]">
                                                    {c.label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={submitting}
                                    data-testid="memorial-form-submit"
                                >
                                    {submitting ? "Wird erstellt…" : "Gedenkseite veröffentlichen"}
                                </button>
                            </form>
                        </div>
                    )}

                    {step === STEP_DONE && (
                        <div className="text-center py-12" data-testid="step-done">
                            <Sparkles size={36} className="text-[#f0c674] mx-auto mb-6" />
                            <h1 className="font-display text-4xl mb-4">Ein neuer Stern leuchtet.</h1>
                            <p className="text-[#94a3b8] mb-8">
                                Die Gedenkseite ist jetzt im Taubenhimmel.
                            </p>
                            {editToken && (
                                <div className="glass p-6 mb-8 text-left max-w-lg mx-auto border-[#f0c674]/30" data-testid="done-edit-link">
                                    <div className="label mb-2">Dein Bearbeitungs-Link</div>
                                    <p className="text-sm text-[#94a3b8] mb-3 leading-relaxed">
                                        Mit diesem Link kannst du die Gedenkseite jederzeit ändern oder löschen.
                                        {ownerEmail ? " Wir haben ihn dir zusätzlich an deine E-Mail geschickt." : " Bitte speichere ihn — er wird nirgendwo sonst angezeigt."}
                                    </p>
                                    <code className="block bg-[#040710]/60 border border-white/10 rounded-lg p-3 text-xs text-[#f0c674] break-all font-mono">
                                        {window.location.origin}/taubenhimmel/bearbeiten/{editToken}
                                    </code>
                                    <button
                                        type="button"
                                        className="mt-3 text-[#f0c674] text-xs hover:underline"
                                        onClick={() => {
                                            navigator.clipboard.writeText(`${window.location.origin}/taubenhimmel/bearbeiten/${editToken}`);
                                            toast.success("Link kopiert");
                                        }}
                                        data-testid="done-copy-link"
                                    >
                                        Link kopieren
                                    </button>
                                </div>
                            )}
                            {unusedCodes.length > 0 && (
                                <div className="glass p-5 mb-6 text-left max-w-lg mx-auto border-[#f0c674]/20" data-testid="done-next-codes">
                                    <div className="label mb-2">
                                        Du hast noch {unusedCodes.length}
                                        {unusedCodes.length === 1 ? " freien Code" : " freie Codes"}
                                    </div>
                                    <p className="text-sm text-[#94a3b8] mb-4 leading-relaxed">
                                        Möchtest du gleich den nächsten Stern eintragen? Du kannst
                                        sie auch später nutzen — wir haben dir alle Codes per Mail
                                        geschickt.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => resetForNextEntry(unusedCodes[0])}
                                        className="btn-primary w-full"
                                        data-testid="done-next-entry"
                                    >
                                        Nächsten Stern eintragen →
                                    </button>
                                </div>
                            )}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <button
                                    onClick={() => navigate(`/taubenhimmel/${savedSlug}`)}
                                    className="btn-primary"
                                    data-testid="done-visit"
                                >
                                    Gedenkseite öffnen
                                </button>
                                <Link to="/taubenhimmel" className="btn-ghost">
                                    Zum Himmel
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </SiteLayout>
    );
}

function ChoiceCard({ title, text, cta, onClick, primary, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`glass gold-glow text-left p-6 transition-all hover:-translate-y-1 ${
                primary ? "border-[#f0c674]/30" : ""
            }`}
            data-testid={testid}
        >
            <h3 className="font-display text-2xl mb-3">{title}</h3>
            <p className="text-[#94a3b8] text-sm leading-relaxed mb-4">{text}</p>
            <span className="text-[#f0c674] text-sm">{cta} →</span>
        </button>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-sm text-[#94a3b8] mb-2">
                {label} {required && <span className="text-[#f0c674]">*</span>}
            </span>
            {children}
        </label>
    );
}
